import { memo, useMemo, useRef, useEffect, useState } from 'react'
import { List, Map } from 'immutable'
import InlineSelect, { InlineSelectLabel } from '@aytm/lib/rfd-ui/components/basic/inline-select'

import useModes from '../../states/useModes'
import useComponentLayout from '../../states/useComponentLayout'

import { DEFAULT_DATA_HEIGHT, DEFAULT_SPLIT_BY, ROW_PADDING } from '../../config/CompareItems_const'
import { CompareItemsTableSplitSelectorCell as TCompareItemsTableSplitSelectorCell } from '../../types'

import style from './SplitSelectorCell.module.scss'

export default memo(
  CompareItemsTableSplitSelectorCell,
  (prevProps, nextProps) =>
    prevProps.width === nextProps.width &&
    prevProps.className === nextProps.className &&
    prevProps.cell === nextProps.cell
)
export function CompareItemsTableSplitSelectorCell({
  className,
  width,
  cell
}: {
  className: string
  width: number
  cell: TCompareItemsTableSplitSelectorCell
}) {
  const { splitGroups, splitBy } = cell

  const modes = useModes()
  const componentLayout = useComponentLayout()
  // the table is measured after its first paint; a reload with a stored split draws this cell before that
  const { dataHeight = DEFAULT_DATA_HEIGHT } = componentLayout.measures

  const fetchSplitByOptions = useMemo(
    () => () =>
      List([
        Map({
          value: null,
          render: DEFAULT_SPLIT_BY
        }),
        ...(splitGroups || []).map(group =>
          Map({
            value: group.id,
            render: group.title
          })
        )
      ]),
    [splitGroups]
  )
  const splitByText = useMemo(() => {
    if (!splitBy) return ''
    const group = splitGroups?.find(g => g.id === splitBy.ref)
    return group ? group.title : ''
  }, [splitBy, splitGroups])

  const selectorLabelRef = useRef<HTMLLabelElement>(null)
  const [selectorLabelHeight, setSelectorLabelHeight] = useState<number>(28)
  useEffect(() => {
    if (selectorLabelRef.current) {
      setSelectorLabelHeight(selectorLabelRef.current.offsetHeight)
    }
  }, [splitByText])

  return (
    <div
      data-testid="stat-compare-items-split-by"
      className={`${className || ''} ${style.CompareItemsTableSplitSelectorCell}`}
      // above the sticky column (10): the label floats up into the row above and the list opens over the rows below
      style={{ flex: `0 0 ${width}px`, width: `${width}px`, zIndex: 11 }}
    >
      {splitGroups?.length && modes.useSplitBy ? (
        <InlineSelectLabel
          className={style.CompareItemsTableSplitSelectorCell__label}
          labelRef={selectorLabelRef}
          labelStyle={{ top: `-${selectorLabelHeight + ROW_PADDING}px` }}
        >
          Split by{splitByText ? <>:&nbsp;</> : null}
          <InlineSelect
            fetchOptions={fetchSplitByOptions}
            onSelect={(option: Map<string, string>) => {
              modes.onChangeSplitBy({
                ref: option?.get('value') || null,
                group: modes.splitBy?.group || 'items'
              })
            }}
            value={splitBy ? splitBy.ref : null}
            maxHeight={Math.max(dataHeight + ROW_PADDING, 65)}
          >
            <b>{splitByText}</b>
          </InlineSelect>
        </InlineSelectLabel>
      ) : null}
    </div>
  )
}
